import React from "react";
import _ from "lodash";
import {
  Text,
  IDropdownOption,
  ILabelStyles,
  Label,
  Spinner,
  SpinnerSize,
  getId,
  ILabelStyleProps,
  IStyleFunctionOrObject,
  ShimmeredDetailsList,
  SelectionMode,
  DetailsListLayoutMode,
  TextField,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
} from "@fluentui/react";
import { UIConstants, isMercuryTenant } from "../../shared/models/Constants";
import AccessAPI from "../../store/AccessAPI";
import ITenant from "../../shared/models/Tenant.model";
import { Policy, EditPolicyData, ApprovalLevels } from "../../shared/models/UserAccess.model";
import {
  ExternalAADRoles,
  Role,
  RoleResponse,
} from "../../shared/models/Role.model";
import { msalAuth } from "../../shared/auth/MsalAuthProvider";
import { IUser } from "../../shared/models/User.model";
import {
  Permission,
  PermissionGroup,
  PermissionLevel,
  PermissionRender
} from "../../shared/models/Mercury";
import { Utility } from "../../shared/models/Helper";
import { RouteComponentProps } from "react-router";
import EditLevelPanel from "./EditLevelPanel";

const labelStyles: Partial<
  IStyleFunctionOrObject<ILabelStyleProps, ILabelStyles>
> = {
  root: { marginTop: 7 },
};

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    marginTop: "50px",
    padding: "1%",
    paddingTop: "2%"
  },
  gridColTitle: {
    paddingLeft: "8px",
    fontSize: 20
  },
  h1: {
    paddingLeft: 0,
    fontSize: 20,
    margin: 0
  },
  gridCol: {
    paddingLeft: "23px"
  },
  select: {
    height: 32,
    width: window.innerWidth < 400 ? 250 : 350
  },
  policyId: {
    fontWeight: 500,
    fontSize: "16px",
    paddingLeft: "15px",
    paddingTop: "40px"
  },
  spinner: {
    paddingLeft: 120,
    paddingTop: 30,
    alignItems: "start"
  },
  submitCol: {
    paddingLeft: "40px",
    marginTop: "16px"
  },
  submitButton: {
    height: 32,
    minWidth: 120,
    background: "#0078d4",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    fontWeight: 600,
    fontSize: "16px",
    cursor: "pointer"
  },
  cancelButton: {
    height: 32,
    minWidth: 120,
    background: "#fff",
    color: "#323130",
    border: "1px solid #8a8886",
    borderRadius: "4px",
    fontWeight: 600,
    fontSize: "16px",
    cursor: "pointer",
    marginLeft: "10px"
  },
  successMessage: {
    color: "green",
    marginTop: "12px",
    fontSize: "14px"
  },
  errorMessage: {
    color: "red",
    marginTop: "12px",
    fontSize: "14px"
  },
  approverButton: {
    marginRight: 8,
    padding: "4px 8px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    background: "#f5f5f5"
  },
  editButton: {
    padding: "4px 12px",
    borderRadius: "4px",
    border: "1px solid #0078d4",
    background: "#f5f5f5",
    fontWeight: 500
  },
  approverRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  approverText: {
    flex: 1,
    wordBreak: "break-word"
  },
  createPolicyContainer: {
    marginTop: "20px",
    padding: "20px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    background: "#f9f9f9"
  },
  buttonGroup: {
    display: "flex",
    gap: "10px",
    marginTop: "16px"
  }
};

export interface IScopeApproversState {
  selectedRole?: Role;
  userDetails: IUser;
  permissionType: string;
  permissionGroup: string;
  permissionLevel: string;
  permissionStructure: Permission[];
  requestorPrincipalId: string;
  selectedPermisisonType: Permission;
  selectedPermisisonGroup: PermissionGroup;
  selectedPermisisonLevel: PermissionLevel;
  permissionLevelList: any;
  tenantData: ITenant;
  permissionMaster: PermissionRender[];
  policy: Policy;
  dataLoaded: boolean;
  RoleDetails: RoleResponse;
  ExternalAADRoleDetails: ExternalAADRoles[];
  isExternalAADRoleDataLoaded: boolean;
  selectedBgRoles: IDropdownOption[];
  businessGrps: any;
  editApprovalPolicyData: EditPolicyData;
  isEditPanelOpen: boolean;
  editLevelIndex: number;
  editLevelData: ApprovalLevels;
  isSubmitting: boolean;
  submitError: string;
  editPanelMode: string;
  userOptions: IDropdownOption[];
  roleName: string;
  successMessage: string;
  showApproverLevels: boolean;
  errorMessage: string;
  permissionValue: string;
  enableSubmit: boolean;
  isCreatingNewPolicy: boolean;
  newPolicyName: string;
  policyNameError: string;
  isValidatingPolicy: boolean;
  showPolicyNameInput: boolean;
}
export interface IScopeApproversProperty {
  tenantData: ITenant;
}

export default class ScopeApprovers extends React.Component<
  IScopeApproversProperty & RouteComponentProps,
  IScopeApproversState
> {
  private tenantData: ITenant = null;
  private submitClicked: boolean = false;
  private _labelId: string = getId("dialogLabel");
  private _subTextId: string = getId("subTextLabel");
  private _roleDropdownItems: IDropdownOption[] = [];
  private _businessgrpDropDownItems: IDropdownOption[] = [];
  private approverLevels: ApprovalLevels[] = [];

  private mapPolicyLevel(level: any): ApprovalLevels {
    return {
      levelId: level?.levelId ?? level?.LevelId ?? 0,
      type: level?.type ?? level?.Type ?? 1,
      category: level?.LevelCategory ?? level?.levelCategory ?? level?.category,
      value: level?.value ?? level?.Value ?? "",
      backupApprovers: level?.backupApprovers ?? undefined,
      externalUsersApproversList:
        level?.externalUsers_ApproversList ??
        level?.externalUsersApproversList ??
        level?.ExternalUsers_ApproversList ??
        level?.ExternalUsersApproversList ??
        undefined,
      additionalInfo: level?.additionalInfo ?? level?.AdditionalInfo ?? undefined,
      externalUsers: level?.external_Users ?? level?.externalUsers ?? level?.External_Users ?? level?.ExternalUsers ?? undefined
    };
  }

  private buildNewLevel(levelId: number): ApprovalLevels {
    return {
      levelId,
      type: 2,
      category: "FTE Manager",
      value: "$FTEManager$",
      backupApprovers: "",
      externalUsersApproversList: undefined,
      additionalInfo: "",
      externalUsers: ""
    };
  }

  public state: IScopeApproversState = {
    selectedRole: {
      typeId: "0",
      type: null,
      groupId: "0",
      group: null,
      id: "0",
      name: null,
      scopes: null,
      description: "",
      permissions: [],
      sortOrder: "",
      attributes: [],
      approvalPolicy: "",
    },
    userDetails: null,
    permissionType: "0",
    permissionGroup: "0",
    permissionLevel: "0",
    permissionStructure: [],
    requestorPrincipalId: "",
    selectedPermisisonType: null,
    selectedPermisisonGroup: null,
    selectedPermisisonLevel: null,
    tenantData: null,
    permissionLevelList: [],
    permissionMaster: [],
    policy: null,
    dataLoaded: true,
    RoleDetails: { Data: [], IsSuccess: false, IsDataLoaded: false },
    ExternalAADRoleDetails: [],
    isExternalAADRoleDataLoaded: false,
    selectedBgRoles: [],
    businessGrps: [],
    isEditPanelOpen: false,
    editLevelIndex: null,
    editLevelData: null,
    isSubmitting: false,
    submitError: "",
    editPanelMode: "add",
    editApprovalPolicyData: { id: "", approvalLevels: [], isCreateNew: false, tenantId: "" },
    userOptions: [],
    roleName:"",
    successMessage: "",
    showApproverLevels: false,
    errorMessage: "",
    permissionValue: "",
    enableSubmit: false,
    isCreatingNewPolicy: false,
    newPolicyName: "",
    policyNameError: "",
    isValidatingPolicy: false,
    showPolicyNameInput: true
  };
  public constructor(props) {
    super(props);
  }
  /**
   * React Life cycle Events
   */
  public async componentDidMount() {
    let userAccount = msalAuth.getAccount();
    let user: IUser = {
      PrincipalId: userAccount.accountIdentifier,
      Alias: userAccount.userName,
      Name: userAccount.name,
    };
    let tenantName = (this.props.match.params as any).tenantName;
    tenantName = tenantName.toUpperCase();
    let TenantData = Utility.GetTenantData();
    this.tenantData = TenantData.filter(
      (x) => x.TenantName.toUpperCase() === tenantName
    )[0];

    let userOptions: IDropdownOption[] = [
      { key: "FTEManager", text: "FTEManager" },
      { key: "Administrator", text: "Administrator" },
      { key: "Manager", text: "Manager" }
    ];

    if (tenantName.toUpperCase() === UIConstants.Tenant.MSSales.toUpperCase()) {
      userOptions.push(
        { key: "MSSalesApprovers", text: "MSSalesApprovers" },
        { key: "MSSalesOEMAdmins", text: "MSSalesOEMAdmins" }
      );
    }

    if (this.tenantData?.isExternalAADTenant || this.tenantData?.EnableRoleGrouping) {
      userOptions.push(
        { key: "Business Owner", text: "Business Owner" },
        { key: "Support", text: "Support" }
      );
    }

    if (tenantName.toUpperCase() === UIConstants.Tenant.PartnerProgramStudio.toUpperCase()) {
      userOptions.push({ key: "Studio Administrator", text: "Studio Administrator" });
    }
    if (tenantName.toUpperCase() === UIConstants.Tenant.PartnerCenter.toUpperCase()) {
      userOptions.push({ key: "HDC Owners", text: "HDC Owners" });
    }
    if (tenantName.toUpperCase() === UIConstants.Tenant.PartnerCenter_WOBO.toUpperCase()) {
      userOptions.push({ key: "Earnings_Business_Owner", text: "Earnings_Business_Owner" });
    }

    this.setState({ userOptions });

    setTimeout(() => {
      this.setState({ userDetails: user, tenantData: this.tenantData });
    }, 500);
    if (isMercuryTenant(tenantName)) {
      AccessAPI.GetMercuryData(tenantName).then((res) => {
        let permissionData: Permission[] = [...res];
        let permissionMasterList: PermissionRender[] = [];
        permissionData.forEach((permissionTypeItem) => {
          permissionTypeItem.permission.forEach((permGrpItem) => {
            permGrpItem.permissionLevels.forEach((permLevelItem) => {
              permissionMasterList.push({
                permissionTypeID: permissionTypeItem.permissionTypeID,
                permissionType: permissionTypeItem.permissionType,
                permissionTypesortOrder: permissionTypeItem.sortOrder,
                permissionGroupID: permGrpItem.permissionGroupID,
                permissionGroup: permGrpItem.permissionGroup,
                permissionGroupDescription:
                  permGrpItem.permissionGroupDescription,
                permissionGroupsortOrder: permGrpItem.sortOrder,
                permissionLevelID: permLevelItem.permissionLevelID,
                permissionLevel: permLevelItem.permissionLevel,
                permissionLevelSortOrder: permLevelItem.sortOrder,
                permissionLevelDescription:
                  permLevelItem.permissionLevelDescription,
                allowEdit: permLevelItem.allowEdit,
                approvalPolicy: permLevelItem.approvalPolicy,
                attribute: permLevelItem.attribute,
                isActive: permLevelItem.isActive,
                role: permLevelItem.role,
              });
            });
          });
        });
        permissionMasterList = _.orderBy(permissionMasterList, [
          "permissionTypesortOrder",
          "permissionGroupsortOrder",
          "permissionLevelSortOrder",
        ]);
        this.setState({
          permissionStructure: res,
          permissionMaster: permissionMasterList,
        });
      });
    } else if (this.tenantData.isExternalAADTenant) {
      AccessAPI.getCosmosRoleDefinitions(this.tenantData.TenantId).then(
        (res) => {
          this.setState({
            ExternalAADRoleDetails: res,
            isExternalAADRoleDataLoaded: true,
          });
        }
      );
    }
    else if (this.tenantData.EnableRoleGrouping) {
      AccessAPI.getBusinessGroupByTenant(this.tenantData.TenantId).then(
        (res) => {
          let bgGrps = res.Data?.businessGroups;
          this.setState({ businessGrps: bgGrps });
        }
      );

      AccessAPI.requestRoleByTenant(this.tenantData.TenantId).then(
        (roleDetails) => {
          this.setState({ RoleDetails: roleDetails });
          // Call API to Get Role
        }
      );
    } else {
      AccessAPI.requestRoleByTenant(this.tenantData.TenantId).then(
        (roleDetails) => {
          this.setState({ RoleDetails: roleDetails });
          // Call API to Get Role
        }
      );
    }
  }

  private handleCreateNewPolicy = () => {
    this.setState({
      isCreatingNewPolicy: true,
      newPolicyName: "",
      policyNameError: "",
      policy: null,
      showApproverLevels: false,
      successMessage: "",
      errorMessage: "",
      enableSubmit: false,
      roleName: "",
      showPolicyNameInput: true
    });
    this.approverLevels = [];
  };

  private handleCancelCreatePolicy = () => {
    this.setState({
      isCreatingNewPolicy: false,
      newPolicyName: "",
      policyNameError: "",
      policy: null,
      showApproverLevels: false,
      enableSubmit: false,
      showPolicyNameInput: true,
      successMessage: "",
      errorMessage: "",
      isEditPanelOpen: false,
      editLevelData: null,
      editLevelIndex: null,
      editPanelMode: "add",
      submitError: "",
      isSubmitting: false
    });
    this.approverLevels = [];
  };

  private validatePolicyName = (value: string): string => {
    if (!value || value.trim() === "") {
      return "Policy name is required";
    }
    if (value.length > 10) {
      return "Policy name must be less than 10 characters";
    }
    if (!/^[a-zA-Z0-9]+$/.test(value)) {
      return "Policy name must contain only alphanumeric characters";
    }
    return "";
  };

  private handlePolicyNameChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    const value = newValue || "";
    const error = this.validatePolicyName(value);
    this.setState({
      newPolicyName: value,
      policyNameError: error,
      enableSubmit: false
    });
  };

  private handleInitializeNewPolicy = async () => {
    const { newPolicyName, tenantData } = this.state;
    const validationError = this.validatePolicyName(newPolicyName);

    if (validationError) {
      this.setState({ policyNameError: validationError });
      return;
    }

    this.setState({ isValidatingPolicy: true, errorMessage: "" });

    try {
      const fullPolicyId = `${tenantData.TenantName}_ApprovalPolicy${newPolicyName}`;

      // Initialize new policy structure with empty levels
      const newPolicy: Policy = {
        id: fullPolicyId,
        name: fullPolicyId,
        tenantId: tenantData.TenantId,
        approval: {
          levels: 0,
          levelInfo: []
        }
      };

      // Start with empty approver levels
      this.approverLevels = [];

      this.setState({
        policy: newPolicy,
        roleName: "",
        showApproverLevels: true,
        isValidatingPolicy: false,
        dataLoaded: true,
        editApprovalPolicyData: { id: fullPolicyId, approvalLevels: [], isCreateNew: true, tenantId: tenantData.TenantId },
        showPolicyNameInput: false,
        isEditPanelOpen: true,
        editLevelIndex: this.approverLevels.length,
        editLevelData: {
          levelId: this.approverLevels.length + 1,
          type: 2,
          value: "FTEManager",
          category: "FTE Manager"
        },
        submitError: "",
        editPanelMode: "add"
      });
    } catch (error) {
      this.setState({
        errorMessage: "Failed to validate policy name. Please try again.",
        isValidatingPolicy: false
      });
    }
  };

  private handleAddNewLevel = () => {
    const nextLevelId = this.approverLevels.length + 1;
    this.setState({
      isEditPanelOpen: true,
      editLevelIndex: this.approverLevels.length,
      editLevelData: {
        levelId: nextLevelId,
        type: 2,
        value: "FTEManager",
        category: "FTE Manager"
      },
      submitError: "",
      editPanelMode: "add"
    });
  };

  public render(): JSX.Element {
    const {
      permissionGroup,
      permissionLevel,
      permissionStructure,
      selectedPermisisonGroup,
      selectedPermisisonLevel,
      permissionLevelList,
      policy,
      RoleDetails,
      selectedRole,
      isSubmitting,
      successMessage,
      errorMessage,
      enableSubmit,
      isEditPanelOpen,
      isCreatingNewPolicy,
      newPolicyName,
      policyNameError,
      isValidatingPolicy,
      showPolicyNameInput
    } = this.state;

    const editPanelKey =
      this.state.policy?.id && this.state.editLevelData?.levelId
        ? `${this.state.policy.id}_${this.state.editLevelData.levelId}`
        : undefined;

    return (
      <React.Fragment>
        <div id={"div-req-section"} style={styles.wrapper}>
          <div key="grid-request" className={"ms-Grid"} dir="ltr">
            <div key="grid-row-pgtitle" className={"ms-Grid-row"}>
              <div className={"ms-Grid-col ms-sm6 ms-md8 ms-lg10"}>
                <Label style={styles.gridColTitle}>
                  {" "}
                  <h1
                    role="heading"
                    aria-level={1}
                    className={"h1"}
                    style={styles.h1}
                  >
                    {" "}
                    {UIConstants.PageTitle.ApprovalPolicy}
                  </h1>
                </Label>
              </div>

              {/* Create New Policy Section */}
              {!isCreatingNewPolicy && (
                <div className={"ms-Grid-col ms-sm12 ms-md8 ms-lg12"} style={styles.gridCol}>
                  <PrimaryButton
                    text="Create Policy"
                    onClick={this.handleCreateNewPolicy}
                    style={{ marginBottom: "20px" }}
                  />
                </div>
              )}

              {isCreatingNewPolicy && showPolicyNameInput && (
                <div className={"ms-Grid-col ms-sm12 ms-md8 ms-lg12"} style={styles.gridCol}>
                  <div style={styles.createPolicyContainer}>
                    <TextField
                      label="Policy Name"
                      placeholder="Enter policy name (alphanumeric, max 10 chars)"
                      value={newPolicyName}
                      onChange={this.handlePolicyNameChange}
                      errorMessage={policyNameError}
                      required
                      maxLength={10}
                      disabled={isValidatingPolicy}
                    />
                    <MessageBar messageBarType={MessageBarType.info} style={{ marginTop: "10px" }}>
                      Full policy ID will be: {this.tenantData?.TenantName}_ApprovalPolicy{newPolicyName || "[Name]"}
                    </MessageBar>
                    <div style={styles.buttonGroup}>
                      <PrimaryButton
                        text="Initialize Policy"
                        onClick={this.handleInitializeNewPolicy}
                        disabled={isValidatingPolicy || !!policyNameError || !newPolicyName}
                      />
                      <DefaultButton
                        text="Cancel"
                        onClick={this.handleCancelCreatePolicy}
                        disabled={isValidatingPolicy}
                      />
                    </div>
                    {isValidatingPolicy && <Spinner label="Validating policy name..." />}
                  </div>
                </div>
              )}

              {/* Existing Role Selection Logic */}
              {!isCreatingNewPolicy && (
                <div className={"ms-Grid-col ms-sm6 ms-md8 ms-lg10"}>
                  {isMercuryTenant(this.tenantData?.TenantName) &&
                    (permissionStructure ? (
                      <div key="grid-row-role" className={"ms-Grid-row"}>
                        <div className={"ms-Grid-col ms-sm12 ms-md8 ms-lg12"} style={styles.gridCol}>
                          <div>
                            <Label required={true} styles={labelStyles}>
                              {UIConstants.Role}
                            </Label>
                            <select
                              id={"drpGrp-pg"}
                              value={permissionGroup}
                              title={UIConstants.PermissionGroup}
                              required={true}
                              onChange={this.onRoleChange}
                              style={styles.select}
                            >
                              <option value="0">-- Select --</option>
                              {this.getPermissionGroups()?.map((x) => {
                                return (
                                  <option
                                    key={"drpGrp-pg" + x.key}
                                    aria-label={x.text}
                                    value={x.key}
                                  >
                                    {x.text}
                                  </option>
                                );
                              })}
                            </select>
                            {selectedPermisisonGroup && (
                              <div>
                                <Text>
                                  {
                                    selectedPermisisonGroup.permissionGroupDescription
                                  }
                                </Text>
                              </div>
                            )}
                          </div>
                        </div>
                        {permissionGroup != "0" &&
                          permissionLevelList != null &&
                          permissionLevelList.length > 0 && (
                            <div className={"ms-Grid-col ms-sm12 ms-md8 ms-lg12"} style={styles.gridCol}>
                              <div>
                                <Label required={true} styles={labelStyles}>
                                  {UIConstants.PermissionScope}
                                </Label>
                                <select
                                  id={"drpGrp-pl"}
                                  value={permissionLevel}
                                  title={UIConstants.Role}
                                  required={true}
                                  onChange={(event) => {
                                    this.onPermissionScopeChange(event);
                                  }}
                                  style={styles.select}
                                >
                                  <option value="0">-- Select --</option>
                                  {permissionLevelList &&
                                    permissionLevelList.map((x, index) => {
                                      return (
                                        <option
                                          key={"drpGrp-pl" + index + x.key}
                                          aria-label={x.text}
                                          value={x.key}
                                        >
                                          {x.text}
                                        </option>
                                      );
                                    })}
                                </select>
                                {selectedPermisisonLevel && (
                                  <div>
                                    <Text>
                                      {
                                        selectedPermisisonLevel.permissionLevelDescription
                                      }
                                    </Text>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    ) : (
                      <Spinner
                        size={SpinnerSize.large}
                        label="loading..."
                        ariaLive="assertive"
                        labelPosition="bottom"
                      />
                    ))}
                  {!isMercuryTenant(this.tenantData?.TenantName) &&
                    (RoleDetails.Data ||
                      this.state.ExternalAADRoleDetails.length > 0 ? (
                      <div>
                        {(this.state.tenantData?.isExternalAADTenant ||
                          this.state.tenantData?.EnableRoleGrouping) && (
                            <div key="grid-row-role" className={"ms-Grid-row"}>
                              <div className={"ms-Grid-col ms-sm12 ms-md8 ms-lg12"} style={styles.gridCol}>
                                <div>
                                  <Label required={true} styles={labelStyles}>
                                    {UIConstants.Group}
                                  </Label>
                                  <select
                                    id={"drpRole"}
                                    title={UIConstants.Group}
                                    required={true}
                                    onChange={(event) =>
                                      this.onBusinessGrpDrpdwnChange(
                                        event.target.value
                                      )
                                    }
                                    style={styles.select}
                                  >
                                    <option value="0">-- Select --</option>
                                    {this.getBusinessGroups().map((x) => {
                                      return (
                                        <option
                                          key={"drpRole" + x.key}
                                          aria-label={x.text}
                                          value={x.key}
                                        >
                                          {x.text}
                                        </option>
                                      );
                                    })}
                                  </select>
                                </div>
                              </div>
                            </div>
                          )}
                        <div key="grid-row-role" className={"ms-Grid-row"}>
                          <div className={"ms-Grid-col ms-sm12 ms-md8 ms-lg12"} style={styles.gridCol}>
                            <div>
                              <Label required={true} styles={labelStyles}>
                                {UIConstants.Role}
                              </Label>
                              <select
                                id={"drpRole"}
                                title={UIConstants.Role}
                                required={true}
                                onChange={(event) =>
                                  this.onRoleDropDownChange(event.target.value)
                                }
                                style={styles.select}
                              >
                                <option value="0">-- Select --</option>
                                {this.state.tenantData &&
                                  (this.state.tenantData?.isExternalAADTenant ||
                                    this.state.tenantData?.EnableRoleGrouping)
                                  ? this.state.selectedBgRoles.map((x) => {
                                    return (
                                      <option
                                        key={"drpRole" + x.key}
                                        aria-label={x.text}
                                        value={x.key}
                                      >
                                        {x.text}
                                      </option>
                                    );
                                  })
                                  : this.getRoles().map((x) => {
                                    return (
                                      <option
                                        key={"drpRole" + x.key}
                                        aria-label={x.text}
                                        value={x.key}
                                      >
                                        {x.text}
                                      </option>
                                    );
                                  })}
                              </select>
                            </div>{" "}
                          </div>
                        </div>{" "}
                      </div>
                    ) : (
                      <Spinner
                        size={SpinnerSize.large}
                        label="loading..."
                        ariaLive="assertive"
                        labelPosition="bottom"
                      />
                    ))}
                  <div key="work-area">
                    {this.state.dataLoaded ? (
                      policy != null && (
                        <>
                          <div style={styles.policyId}>
                            Policy ID: {policy.id ?? ""}
                          </div>
                          <ShimmeredDetailsList
                            setKey="scopeapprover-items"
                            columns={this.ApproverDataCoulmns()}
                            items={this.getPolicyItems()}
                            layoutMode={DetailsListLayoutMode.justified}
                            selectionMode={SelectionMode.none}
                            enableShimmer={false}
                            ariaLabelForShimmer="Scope Approver details is being fetched"
                            ariaLabelForGrid="Scope Approver details"
                            detailsListStyles={{ root: { overflow: "visible" } }}
                            listProps={{
                              renderedWindowsAhead: 10,
                              renderedWindowsBehind: 10,
                            }}
                          />
                        </>
                      )
                    ) : (
                      <Spinner
                        size={SpinnerSize.large}
                        style={styles.spinner}
                        label="loading..."
                        ariaLive="assertive"
                        labelPosition="bottom"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Create Policy Work Area - Same structure as Update */}
              {isCreatingNewPolicy && !showPolicyNameInput && policy && (
                <div className={"ms-Grid-col ms-sm12 ms-md8 ms-lg12"}>
                  <div style={styles.policyId}>
                    Policy ID: {policy.id ?? ""}
                  </div>
                  <>
                    <ShimmeredDetailsList
                      setKey="scopeapprover-items-create"
                      columns={this.ApproverDataCoulmns()}
                      items={this.getPolicyItems()}
                      layoutMode={DetailsListLayoutMode.justified}
                      selectionMode={SelectionMode.none}
                      enableShimmer={false}
                      ariaLabelForShimmer="Scope Approver details is being fetched"
                      ariaLabelForGrid="Scope Approver details"
                      detailsListStyles={{ root: { overflow: "visible" } }}
                      listProps={{
                        renderedWindowsAhead: 10,
                        renderedWindowsBehind: 10,
                      }}
                    />
                  </>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Edit Level Panel UI */}
        {this.state.isEditPanelOpen && (
          <EditLevelPanel
            key={editPanelKey}
            mode={this.state.editPanelMode}
            initialData={this.state.editLevelData}
            userOptions={this.state.userOptions}
            onClose={() => this.setState({ isEditPanelOpen: false })}
            onSubmit={levelData => {
              const idx = this.approverLevels.findIndex(l => l.levelId === levelData.levelId);
              let updatedLevels = [...this.approverLevels];
              if (idx !== -1) {
                updatedLevels[idx] = { ...this.approverLevels[idx], ...levelData };
              } else if (typeof this.state.editLevelIndex === "number") {
                updatedLevels[this.state.editLevelIndex] = { ...this.approverLevels[this.state.editLevelIndex], ...levelData };
              } else {
                updatedLevels.push({ ...levelData });
              }
              this.approverLevels = updatedLevels;
              this.setState({
                isEditPanelOpen: false,
                editLevelIndex: null,
                editLevelData: null,
                submitError: "",
                editPanelMode: "add",
                showApproverLevels: true,
                enableSubmit: true
              });
            }}
            approverLevels={this.approverLevels}
            submitError={this.state.submitError}
          />
        )}
        <div key="grid-row-submit" className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md8 ms-lg12" style={styles.submitCol}>
            {((this.isSubmitEnabled() && this.state.dataLoaded && policy != null) || (isCreatingNewPolicy && !showPolicyNameInput && policy)) && (
              <div>
                {isSubmitting && <Spinner label="Saving..." />}
                <button
                  onClick={this.handleSaveApprovers}
                  disabled={isSubmitting || !enableSubmit}
                  style={{
                    ...styles.submitButton,
                    cursor: enableSubmit ? "pointer" : "not-allowed",
                    opacity: enableSubmit ? 1 : 0.6,
                    pointerEvents: enableSubmit ? "auto" : "none"
                  }}
                >
                  Submit
                </button>
                {/* Cancel button only for create policy mode */}
                {isCreatingNewPolicy && (
                  <button
                    onClick={this.handleCancelCreatePolicy}
                    disabled={isSubmitting}
                    style={{
                      ...styles.cancelButton,
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      opacity: isSubmitting ? 0.6 : 1
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            )}
            {successMessage && (
              <div style={styles.successMessage}>
                {successMessage}
              </div>
            )}
            {errorMessage && (
              <div style={styles.errorMessage}>
                {errorMessage}
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
  private getBusinessGroups() {
    const { tenantData, ExternalAADRoleDetails, businessGrps } = this.state;
    let uniqueItems;
    if (tenantData?.isExternalAADTenant) {
      this._businessgrpDropDownItems = ExternalAADRoleDetails.map((x) => {
        return { key: x.businessGroupId, text: x.businessGroupName };
      });
      uniqueItems = this._businessgrpDropDownItems.filter(
        (item, index) =>
          this._businessgrpDropDownItems.findIndex(
            (x) => x.text == item.text
          ) == index
      );
    } else if (tenantData?.EnableRoleGrouping) {
      this._businessgrpDropDownItems = businessGrps.map((x) => {
        return { key: x.businessGroupId, text: x.businessGroupName };
      });
      uniqueItems = this._businessgrpDropDownItems.filter(
        (item, index) =>
          this._businessgrpDropDownItems.findIndex(
            (x) => x.text == item.text
          ) == index
      );
    }
    uniqueItems.sort((x, y) => {
      if (x.text.toUpperCase() > y.text.toUpperCase()) {
        return -1;
      } else if (x.text.toUpperCase() < y.text.toUpperCase()) {
        return 1;
      } else {
        return 0;
      }
    });
    let index = uniqueItems.findIndex((x) => x.text == "Global_Roles");
    if (index != -1) {
      let globalRoles = uniqueItems[index];
      uniqueItems.splice(index, 1);
      uniqueItems.push(globalRoles);
    }
    return uniqueItems.reverse();
  }
  private onBusinessGrpDrpdwnChange(value: any) {
    const { ExternalAADRoleDetails, tenantData, businessGrps } = this.state;
    let selectedBusinessGrpId = value;
    let roles;
    if (value == "0") {
      this.setState({
        permissionGroup: "",
        permissionLevel: "",
        policy: null,
        selectedPermisisonLevel: null,
        permissionLevelList: null,
        selectedRole: {
          typeId: "0",
          type: null,
          groupId: "0",
          group: null,
          id: "0",
          name: null,
          scopes: null,
          description: "",
          permissions: [],
          sortOrder: "",
          attributes: [],
          approvalPolicy: "",
        },
        editApprovalPolicyData: { id: "", approvalLevels: [], isCreateNew: false, tenantId: "" },
        editLevelData: null,
        isEditPanelOpen: false,
        editLevelIndex: null,
        submitError: "",
        successMessage: "",
        editPanelMode: "add",
        roleName: "",
        showApproverLevels: false,
        errorMessage: "",
        permissionValue: "",
        selectedBgRoles: [],
        enableSubmit: false
      });
    } else {
      if (tenantData?.isExternalAADTenant) {
        let bgName = ExternalAADRoleDetails.find(
          (x) => x.businessGroupId == value
        ).businessGroupName;
        roles = ExternalAADRoleDetails.filter(
          (x) => x.businessGroupName == bgName
        ).map((x) => {
          return { key: x.roleId, text: x.roleName };
        });
      } else if (tenantData?.EnableRoleGrouping) {
        let bgName = businessGrps.find(
          (x) => x.businessGroupId == selectedBusinessGrpId
        ).businessGroupName;
        let groupSpecificRoles = businessGrps
          .filter((x) => x.businessGroupName == bgName)
          .map((x) => x.roles);
        roles = groupSpecificRoles[0].map((x) => {
          return { key: x.roleId, text: x.roleName };
        });
      }
      roles.sort((a, b) => {
        if (a.text.toUpperCase() < b.text.toUpperCase()) {
          return -1;
        } else if (a.text.toUpperCase() > b.text.toUpperCase()) {
          return 1;
        } else {
          return 0;
        }
      });
      this.setState({
        selectedBgRoles: roles,
        isEditPanelOpen: false,
        showApproverLevels: false,
        submitError: "",
        successMessage: "",
        errorMessage: "",
        enableSubmit: false
      });
    }
  }
  private onRoleDropDownChange = (value: any): void => {
    const { RoleDetails, tenantData, ExternalAADRoleDetails, businessGrps } =
      this.state;
    if (value == "0") {
      this.setState({
        permissionGroup: "",
        permissionLevel: "",
        policy: null,
        selectedPermisisonLevel: null,
        permissionLevelList: null,
        selectedRole: {
          typeId: "0",
          type: null,
          groupId: "0",
          group: null,
          id: "0",
          name: null,
          scopes: null,
          description: "",
          permissions: [],
          sortOrder: "",
          attributes: [],
          approvalPolicy: "",
        },
        editApprovalPolicyData: { id: "", approvalLevels: [], isCreateNew: false, tenantId: "" },
        editLevelData: null,
        isEditPanelOpen: false,
        editLevelIndex: null,
        submitError: "",
        successMessage: "",
        editPanelMode: "add",
        roleName: "",
        showApproverLevels: false,
        errorMessage: "",
        permissionValue: "",
        enableSubmit: false
      });
    } else {
      if (tenantData.isExternalAADTenant) {
        let selectedRoleKey = value;
        let policyName = ExternalAADRoleDetails.find(
          (x) => x.roleId == selectedRoleKey
        ).policyType;
        this.setState({
          roleName: ExternalAADRoleDetails.find(
            (x) => x.roleId == selectedRoleKey
          )?.roleName,
          isEditPanelOpen: false,
          showApproverLevels: false,
          submitError: "",
          successMessage: "",
          errorMessage: "",
          enableSubmit: false
        });
        this.GetPolicyDetails(policyName.split(":")[1].trim());
      } else {
        let currentRole = RoleDetails.Data.filter((x) => x.id === value)[0];
        this.setState({
          roleName: currentRole?.name,
          isEditPanelOpen: false,
          showApproverLevels: false,
          submitError: "",
          successMessage: "",
          errorMessage: "",
          enableSubmit: false
        });
        let approvalPolicy =
          currentRole.permissions[0].actions[0].split("Policy:")[1];
        this.GetPolicyDetails(approvalPolicy);
      }
    }
  };
  private getRoles = (): IDropdownOption[] => {
    const { RoleDetails, tenantData, ExternalAADRoleDetails } = this.state;
    this._roleDropdownItems = RoleDetails.Data.map((x) => {
      return { key: x.id, text: x.name };
    });
    return this._roleDropdownItems;
  };
  private onRoleChange = (event) => {
    const { permissionStructure, permissionMaster } = this.state;
    let plList: any = [];
    if (event.target.value == "0") {
      this.setState({
        permissionGroup: event.target.value,
        permissionLevel: "",
        policy: null,
        selectedPermisisonLevel: null,
        permissionLevelList: plList,
        selectedRole: {
          typeId: "0",
          type: null,
          groupId: "0",
          group: null,
          id: "0",
          name: null,
          scopes: null,
          description: "",
          permissions: [],
          sortOrder: "",
          attributes: [],
          approvalPolicy: "",
        },
        editApprovalPolicyData: { id: "", approvalLevels: [], isCreateNew: false, tenantId: "" },
        editLevelData: null,
        isEditPanelOpen: false,
        editLevelIndex: null,
        submitError: "",
        successMessage: "",
        editPanelMode: "add",
        roleName: "",
        showApproverLevels: false,
        errorMessage: "",
        permissionValue: "",
        enableSubmit: false
      });
    } else {
      let selectedPermGrp = permissionMaster.filter(
        (x) => x.permissionGroupID == event.target.value
      );
      if (selectedPermGrp.length != 1) {
        plList = selectedPermGrp.map((x) => {
          return { key: x.permissionLevelID, text: x.permissionLevel };
        });
        plList = _.uniqBy(plList, "key");
        this.setState({
          permissionGroup: event.target.value,
          permissionLevel: "0",
          policy: null,
          selectedPermisisonLevel: null,
          permissionLevelList: plList,
          selectedRole: {
            typeId: "0",
            type: null,
            groupId: "0",
            group: null,
            id: "0",
            name: null,
            scopes: null,
            description: "",
            permissions: [],
            sortOrder: "",
            attributes: [],
            approvalPolicy: "",
          },
          editApprovalPolicyData: { id: "", approvalLevels: [], isCreateNew: false, tenantId: "" },
          editLevelData: null,
          isEditPanelOpen: false,
          editLevelIndex: null,
          submitError: "",
          successMessage: "",
          editPanelMode: "add",
          roleName: "",
          errorMessage: "",
          permissionValue: ""
        });
      } else {
        plList = [];
        this.setState({
          permissionGroup: event.target.value,
          permissionLevel: permissionMaster.filter(
            (x) => x.permissionGroupID == event.target.value
          )[0].permissionLevelID,
          permissionLevelList: plList,
          policy: null,
          selectedRole: {
            typeId: permissionMaster.filter(
              (x) => x.permissionGroupID == event.target.value
            )[0].permissionTypeID,
            type: permissionMaster.filter(
              (x) => x.permissionGroupID == event.target.value
            )[0].permissionType,
            groupId: permissionMaster.filter(
              (x) => x.permissionGroupID == event.target.value
            )[0].permissionGroupID,
            group: permissionMaster.filter(
              (x) => x.permissionGroupID == event.target.value
            )[0].permissionGroup,
            id: permissionMaster.filter(
              (x) => x.permissionGroupID == event.target.value
            )[0].permissionLevelID,
            name: permissionMaster.filter(
              (x) => x.permissionGroupID == event.target.value
            )[0].permissionLevel,
            scopes: null,
            description: "",
            permissions: [],
            sortOrder: "",
            attributes: [],
            approvalPolicy: permissionMaster.filter(
              (x) => x.permissionGroupID == event.target.value
            )[0].approvalPolicy,
          },
          editApprovalPolicyData: { id: "", approvalLevels: [], isCreateNew: false, tenantId: "" },
          editLevelData: null,
          isEditPanelOpen: false,
          editLevelIndex: null,
          submitError: "",
          successMessage: "",
          editPanelMode: "add",
          roleName: "",
          showApproverLevels: false,
          errorMessage: "",
          permissionValue: "",
          enableSubmit: false
        });
        let approvalPolicy = permissionMaster.filter(
          (x) => x.permissionGroupID == event.target.value
        )[0].approvalPolicy;
        this.GetPolicyDetails(approvalPolicy);
      }
    }
  };

  private getPermissionGroups = (): IDropdownOption[] => {
    const { permissionMaster } = this.state;
    let permissionGroupList = permissionMaster.map((y) => {
      return { key: y.permissionGroupID, text: y.permissionGroup };
    });
    permissionGroupList = _.uniqBy(permissionGroupList, "key");
    return permissionGroupList;
  };

  private onPermissionScopeChange(event) {
    const { permissionStructure, permissionMaster, permissionValue } = this.state;
    this.setState({ permissionLevel: event.target.value, policy: null });
    let selectedPermissionLevel = permissionMaster.filter(
      (x) => x.permissionLevelID == event.target.value
    )[0];
    let permissionLevels = permissionStructure
      ?.filter(
        (x) => x.permissionTypeID == selectedPermissionLevel.permissionTypeID
      )[0]
      ?.permission.filter(
        (x) => x.permissionGroupID == selectedPermissionLevel.permissionGroupID
      )[0]
      ?.permissionLevels.filter(
        (x) => x.permissionLevelID == selectedPermissionLevel.permissionLevelID
      )[0];
    this.setState({
      selectedRole: {
        id: event.target.value,
        name: null,
        scopes: [],
        description: "",
        permissions: [],
        sortOrder: "",
        attributes: [],
        approvalPolicy: permissionLevels.approvalPolicy
      },
      permissionValue: permissionLevels.permissionLevel,
      isEditPanelOpen: false,
      showApproverLevels: false,
      submitError: "",
      successMessage: "",
      errorMessage: "",
      enableSubmit: false
    });
    this.GetPolicyDetails(permissionLevels.approvalPolicy);
  }

  private GetPolicyDetails(approvalPolicy: any) {
    const { policy } = this.state;
    this.setState({ dataLoaded: false });
    AccessAPI.GetPolicyBasedOnPolicyId(approvalPolicy).then((res) => {
      this.setState({ policy: res, dataLoaded: true });
    });
  }

  private getPolicyItems = () => {
    const { policy } = this.state;
    let policyData = [];
    let policyItems = [];
    if (this.state.showApproverLevels && this.approverLevels.length > 0) {
      return this.approverLevels.map(level => ({
        code: "-",
        description: "-",
        approvers:
          level.category
            ? level.type === 1
              ? "Level " + level.levelId + " : " + level.category + " - " + level.value
              : "Level " + level.levelId + " : " + level.category
            : "Level " +
            level.levelId + " : " + level.value
      }));
    }
    if (
      (!this.state.showApproverLevels) &&
      policy != null &&
      policy.approval.levelInfo != null &&
      policy.approval.levelInfo.length > 0
    ) {
      let approverTypeFlag = policy.approval.levelInfo.filter(
        (x) => x.type == 3
      );
      let approverDetails = "";
      if (approverTypeFlag != null) {
        let approverTypeDetails = policy.approval.levelInfo.filter(
          (x) => x.type != 3
        );
        for (const approval of approverTypeDetails) {
          approverDetails +=
            "Level " + approval.levelId + " : " + approval.LevelCategory + "; ";
        }
      }
      for (const approval of policy.approval.levelInfo) {
        policyItems.push(this.mapPolicyLevel(approval));
        if (approval.levelScope != null && approval.type == 3) {
          policyData.push({
            code: approval.levelScope[0].code,
            description: approval.levelScope[0].description,
            approvers:
              approverDetails +
              "Level " +
              approval.levelId +
              "-" +
              approval.LevelCategory +
              " : " +
              approval.value,
            levelInfo: approval
          });
        } else {
          if (approverTypeFlag == null || approverTypeFlag.length == 0) {
            policyData.push({
              code: "-",
              description: "-",
              approvers:
                approval.LevelCategory != null
                  ? approval.type == 1
                    ? "Level " +
                    approval.levelId +
                    " : " +
                    approval.LevelCategory +
                    " - " +
                    approval.value
                    : "Level " +
                    approval.levelId +
                    " : " +
                    approval.LevelCategory
                  : "Level " +
                  approval.levelId +
                  " : " +
                  approval.value.replaceAll("$", ""),
              levelInfo: approval
            });
          }
        }
      }
      this.approverLevels = policyItems;
    }
    return policyData;
  };

  private handleRemoveLevel = (idx) => {
    if (this.approverLevels.length <= 1) return;
    const updatedLevels = [...this.approverLevels];
    updatedLevels.splice(idx, 1);
    this.approverLevels = updatedLevels;
    this.setState({ showApproverLevels: true, enableSubmit: true });
  };

  private ApproverDataCoulmns = () => {
    const { policy, isCreatingNewPolicy } = this.state;
    let columnDef = [];
    if (
      !isCreatingNewPolicy &&
      policy != null &&
      policy.approval.levelInfo != null &&
      policy.approval.levelInfo.length > 0 &&
      policy.approval.levelInfo.filter((x) => x.type == 3)?.length > 0
    ) {
      columnDef.push({
        key: "code",
        name: "Code",
        fieldName: "code",
        minWidth: 120,
        maxWidth: 180,
        isResizable: true,
      });
      columnDef.push({
        key: "description",
        name: "Description",
        fieldName: "description",
        minWidth: 320,
        maxWidth: 320,
        isMultiline: true,
        isResizable: true,
      });
    }
    columnDef.push({
      key: "approvers",
      name: "Approvers",
      fieldName: "approvers",
      minWidth: 600,
      maxWidth: 600,
      isMultiline: true,
      isResizable: true,
      onRender: (item, idx) => {
        const isLast = idx === this.approverLevels.length - 1;
        const canEdit = isCreatingNewPolicy || this.isEditEnabled();

        return (
          <div style={styles.approverRow}>
            {isLast && (
              <>
                <button
                  disabled={!canEdit || this.approverLevels.length <= 1}
                  onClick={e => { e.stopPropagation(); this.handleRemoveLevel(idx); }}
                  style={styles.approverButton}
                  aria-label="Remove Level"
                >-</button>
                <button
                  disabled={!canEdit}
                  onClick={e => {
                    e.stopPropagation();
                    this.setState({
                      isEditPanelOpen: true,
                      editLevelIndex: idx + 1,
                      editLevelData: this.buildNewLevel(idx + 2),
                      submitError: "",
                      editPanelMode: "add"
                    });
                  }}
                  style={styles.approverButton}
                  aria-label="Add Level"
                >+</button>
              </>
            )}
            <button
              disabled={!canEdit}
              onClick={e => {
                e.stopPropagation();
                this.setState({
                  isEditPanelOpen: true,
                  editLevelIndex: idx,
                  editLevelData: this.approverLevels[idx],
                  submitError: "",
                  editPanelMode: "edit"
                });
              }}
              style={styles.editButton}
              aria-label={`Edit approver for level ${item.levelInfo?.levelId || idx + 1}`}
            >
              Edit
            </button>
            <span style={styles.approverText}>
              {item.approvers}
            </span>
          </div>
        );
      },
    });
    return columnDef;
  };

  private handleSaveApprovers = () => {
    this.setState({ isSubmitting: true, errorMessage: "", successMessage: "", enableSubmit: false });
    if (!this.approverLevels || this.approverLevels.length === 0) {
      this.setState({
        isSubmitting: false,
        errorMessage: "Policy must have at least one approval level",
        enableSubmit: false
      });
      return;
    }
    const { policy, editApprovalPolicyData } = this.state;
    editApprovalPolicyData.id = policy.id;
    editApprovalPolicyData.approvalLevels = this.approverLevels.map(level => ({
      ...level,
      category: level.category ?? ""
    }));
    editApprovalPolicyData.tenantId = policy.tenantId;
    editApprovalPolicyData.isCreateNew = this.state.isCreatingNewPolicy;
    AccessAPI.updatePolicyDetails(editApprovalPolicyData)
      .then((res) => {
        if (res.status !== 200) {
          this.setState({ isSubmitting: false, errorMessage: res.message || "Failed to save", enableSubmit: true });
        } else {
          this.approverLevels = [];
          this.setState({
            isSubmitting: false,
            successMessage: this.state.isCreatingNewPolicy ? "Policy created successfully" : "Policy is updated successfully",
            isCreatingNewPolicy: false,
            newPolicyName: "",
            policyNameError: "",
            isValidatingPolicy: false,
            showPolicyNameInput: true,
            showApproverLevels: false
          });
          this.GetPolicyDetails(policy.id);
        }
      })
      .catch((err) => {
        this.setState({ isSubmitting: false, errorMessage: err.message || "Failed to save", enableSubmit: true });
      });
  };

  private isSubmitEnabled(): boolean {
    const { permissionGroup, permissionLevelList, tenantData, roleName, permissionValue } = this.state;
    const tenantName = tenantData?.TenantName?.toUpperCase();

    if (isMercuryTenant(tenantName)) {
      const permissionLevel = permissionGroup !== "0" && permissionLevelList != null && permissionLevelList.length > 0;
      if (permissionLevel) {
        return (
          permissionValue.toLowerCase() === "consolidated gaap" ||
          permissionValue.toLowerCase() === "all companies"
        );
      } else if (permissionGroup !== "0") {
        return true;
      } else {
        return false;
      }
    }

    if ((tenantData?.isExternalAADTenant || tenantData?.EnableRoleGrouping) && roleName != "") {
      return true;
    }

    if (tenantName === UIConstants.Tenant.Alfred.toUpperCase() && roleName !== "") {
      return !roleName?.toLowerCase().includes("write");
    }
    if (tenantName === UIConstants.Tenant.MyOrder.toUpperCase() && roleName !== "") {
      if (roleName?.toLowerCase() == "po buydesk viewer" || roleName?.toLowerCase() == "fsn (finops support network)" || roleName?.toLowerCase() == "business admin") {
        return false;
      }
      return true;
    }
    return roleName != "";
  }

  private isEditEnabled(): boolean {
    const { tenantData, permissionGroup, permissionLevelList, roleName, permissionValue } = this.state;
    const tenantName = tenantData?.TenantName?.toUpperCase();

    if (isMercuryTenant(tenantName)) {
      const permissionLevel = permissionGroup !== "0" && permissionLevelList != null && permissionLevelList.length > 0;
      if (permissionLevel) {
        return (
          permissionValue.toLowerCase() === "consolidated gaap" ||
          permissionValue.toLowerCase() === "all companies"
        );
      }
      return true;
    }

    if (tenantName === UIConstants.Tenant.Alfred.toUpperCase()) {
      return !roleName?.toLowerCase().includes("write");
    }
    if (tenantName === UIConstants.Tenant.MyOrder.toUpperCase()) {
      if (roleName?.toLowerCase() == "po buydesk viewer" || roleName?.toLowerCase() == "fsn (finops support network)" || roleName?.toLowerCase() == "business admin") {
        return false;
      }
      return true;
    }
    return true;
  }
}
